import React from "react";
import styled from "styled-components";
import AddCircleOutline from "@material-ui/icons/AddCircleOutline";
import ExitToApp from "@material-ui/icons/ExitToApp";
import Notifications from "@material-ui/icons/Notifications";
import { History } from "history";
import { intersection } from "lodash";
import { match as Match, RouteComponentProps, withRouter } from "react-router";
import {
  History as HistoryIcon,
  Reporting as ReportingIcon,
  Schedule as ScheduleIcon,
  Task as TaskIcon,
  Team as TeamIcon,
} from "@sonder/ui/source/icons";
import { Capsule } from "@sonder/ui/source/styled/fragments/Capsule";
import {
  SidebarBackdrop,
  SidebarButton,
  SidebarDivider,
  Sidebar as SidebarFragment,
  SidebarPusher,
  SidebarSpacer,
  SidebarStatic,
} from "@sonder/ui/source/styled/fragments/Sidebar";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { useCanViewCheckouts } from "checkouts/hooks/useCanViewCheckouts";
import { CityPicker } from "core/components/CityPicker";
import { CabinetDrawers, useLayout } from "core/components/LayoutProvider";
import { useGetNotificationsTotalPollingQuery } from "core/queries/GetNotificationsTotal";
import { setCreateTaskModalState } from "tasks/actions";
import { useStore as useTasksStore } from "tasks/store";
import { DispatchRoleName } from "typings/globalGQLTypes";
import { UserMenu } from "users/components/UserMenu";

const UnreadWrapper = styled.span`
  position: relative;
`;

const TaskCapsule = styled(Capsule)`
  position: absolute;
  right: 0;
  top: 0;

  transform: translate(75%, -37.5%) scale(0.75);
`;

const onClick = (history: History, match: Match, route: string) => {
  if (!match.path.startsWith(route)) history.push(route);
};

const HistoryButton = withRouter(({ history, match }: RouteComponentProps) => (
  <SidebarButton
    selected={match.path.startsWith("/history")}
    title="Task history"
    onClick={() => onClick(history, match, "/history")}
  >
    <HistoryIcon />
  </SidebarButton>
));

const ReportsButton = withRouter(({ history, match }: RouteComponentProps) => {
  const { state: authState } = useAuthStore();

  const userRoles = authState.user?.roles || [];
  const enabledRoles = [
    DispatchRoleName.tom_manager,
    DispatchRoleName.tom_team_lead,
  ];

  if (intersection(userRoles, enabledRoles).length === 0) return null;

  return (
    <SidebarButton
      selected={match.path.startsWith("/reports")}
      title="City reports"
      onClick={() => onClick(history, match, "/reports")}
    >
      <ReportingIcon />
    </SidebarButton>
  );
});

const ScheduleButton = withRouter(({ history, match }: RouteComponentProps) => {
  const {
    state: { user },
  } = useAuthStore();
  const canViewScheduler = user?.isHKManager || user?.isCleaningManager;

  if (!canViewScheduler) return null;

  return (
    <SidebarButton
      selected={match.path.startsWith("/schedule")}
      title="Schedule"
      onClick={() => onClick(history, match, "/schedule")}
    >
      <ScheduleIcon />
    </SidebarButton>
  );
});

const TasksButton = withRouter(({ history, match }: RouteComponentProps) => (
  <SidebarButton
    selected={match.path.startsWith("/tasks")}
    title="Your tasks"
    onClick={() => onClick(history, match, "/tasks")}
  >
    <TaskIcon />
  </SidebarButton>
));

const TeamButton = withRouter(({ history, match }: RouteComponentProps) => (
  <SidebarButton
    selected={match.path.startsWith("/team")}
    title="Your team"
    onClick={() => onClick(history, match, "/team")}
  >
    <TeamIcon />
  </SidebarButton>
));

const CheckoutsButton = withRouter(
  ({ history, match }: RouteComponentProps) => {
    const { canViewCheckouts } = useCanViewCheckouts();

    return canViewCheckouts ? (
      <SidebarButton
        selected={match.path.startsWith("/checkouts")}
        title="Checkouts"
        onClick={() => onClick(history, match, "/checkouts")}
      >
        <ExitToApp />
      </SidebarButton>
    ) : null;
  },
);

const CreateTaskButton = () => {
  const { state, dispatch } = useTasksStore();
  const { track } = useSegment();

  return (
    <SidebarButton
      disabled={!state.filters.city}
      title="Create task"
      onClick={() => {
        track({
          eventName: "view_header",
          action: "click",
          element: "create_task_button",
        });
        dispatch(setCreateTaskModalState({ isOpen: true }));
      }}
    >
      <AddCircleOutline />
    </SidebarButton>
  );
};

const NotificationsButton = () => {
  const { state: authState } = useAuthStore();
  const { setOpenCabinetDrawers } = useLayout();
  const { track } = useSegment();
  const userId = authState.user?.id;
  const { data } = useGetNotificationsTotalPollingQuery({
    skip: !userId,
  });
  const { total = 0 } = data?.userNotificationTotal || {};
  return (
    <SidebarButton
      disabled={!userId}
      title="Notifications"
      onClick={() => {
        requestAnimationFrame(() => {
          setOpenCabinetDrawers([CabinetDrawers.NotificationCenter]);
        });
        track({
          eventName: "notification_center",
          action: "click",
          element: "open",
        });
      }}
    >
      <UnreadWrapper>
        <Notifications />
        {total > 0 && <TaskCapsule>{total > 99 ? `99+` : total}</TaskCapsule>}
      </UnreadWrapper>
    </SidebarButton>
  );
};

export const Sidebar = React.forwardRef(
  (props, ref: React.Ref<HTMLElement>) => {
    const { sidebar, setSidebar } = useLayout();

    const expanded = sidebar === "expanded";
    return (
      <>
        <SidebarBackdrop
          expanded={expanded}
          onClick={() => setSidebar("collapsed")}
        />
        <SidebarFragment expanded={expanded} ref={ref}>
          <SidebarStatic>D</SidebarStatic>
          <TasksButton />
          <TeamButton />
          <ScheduleButton />
          <ReportsButton />
          <HistoryButton />
          <CheckoutsButton />
          <SidebarSpacer>
            <SidebarDivider />
          </SidebarSpacer>
          <CreateTaskButton />
          <SidebarPusher />
          <NotificationsButton />
          <CityPicker />
          <SidebarSpacer>
            <SidebarDivider />
          </SidebarSpacer>
          <UserMenu />
        </SidebarFragment>
      </>
    );
  },
);
